import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Shield } from "lucide-react";
import { apiGet, apiPost, ApiError } from "../lib/api.js";
import { Panel } from "../components/Panel.js";
import { useAuth } from "../auth/AuthContext.js";
import { ROLE_LABELS, ROLE_DESCRIPTIONS, type ProgrammeRole } from "./roles.js";

const inputClass = "w-full max-w-sm rounded-md border px-2 py-1.5 text-sm";
const labelClass = "mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground";

/**
 * "Basic administration" for the signed-in account, reached from the user menu rather than the
 * sidebar — it is about *you*, not a section of the platform. Role and programme access are shown
 * read-only here; changing them is a superadmin/programme_admin action on the Users screen, not
 * something an account manages for itself.
 */
export const ProfilePage = (): JSX.Element => {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const programmesQuery = useQuery({
    queryKey: ["programmes"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/programmes"),
  });
  const programmeNameById = new Map((programmesQuery.data?.data ?? []).map((p) => [p.id, p.name]));

  const changePassword = useMutation({
    mutationFn: () =>
      apiPost("/auth/password/change", { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    },
    onError: (err) => {
      setSuccess(false);
      setError(err instanceof ApiError ? err.message : "Could not change your password.");
    },
  });

  if (!user) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <header>
        <h1 className="font-heading text-2xl font-semibold">My profile</h1>
        <p className="text-sm text-muted-foreground">Your account, access and password.</p>
      </header>

      <Panel title="Account">
        <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Name</dt>
          <dd className="font-medium">{user.fullName}</dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd className="font-medium">{user.email}</dd>
          <dt className="text-muted-foreground">Sensitive data</dt>
          <dd>{user.canViewPii ? "Can view unmasked" : "Masked (default)"}</dd>
        </dl>
        {user.isSuperadmin && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-primary">
            <Shield aria-hidden className="h-3.5 w-3.5" />
            Superadmin — full access to every programme and platform setting.
          </p>
        )}
      </Panel>

      {!user.isSuperadmin && (
        <Panel title="Programme access">
          {user.programmeRoles.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No programme roles yet — ask a superadmin or programme admin to add you.
            </p>
          ) : (
            <ul className="space-y-2">
              {user.programmeRoles.map((role) => (
                <li
                  key={role.programmeId}
                  className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <span className="font-medium">
                    {programmeNameById.get(role.programmeId) ?? role.programmeId}
                  </span>
                  <span className="text-right">
                    <span className="block">{ROLE_LABELS[role.role as ProgrammeRole]}</span>
                    <span className="block text-xs text-muted-foreground">
                      {ROLE_DESCRIPTIONS[role.role as ProgrammeRole]}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <Panel title="Change password">
        {success && (
          <p className="mb-3 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
            Password changed. Every other session was signed out.
          </p>
        )}
        {error && (
          <p className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            if (next !== confirm) {
              setError("New password and confirmation don't match.");
              return;
            }
            changePassword.mutate();
          }}
        >
          <div>
            <label htmlFor="current-password" className={labelClass}>
              Current password
            </label>
            <input
              id="current-password"
              type="password"
              required
              autoComplete="current-password"
              className={inputClass}
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="new-password" className={labelClass}>
              New password
            </label>
            <input
              id="new-password"
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              className={inputClass}
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className={labelClass}>
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              className={inputClass}
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={changePassword.isPending}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {changePassword.isPending ? "Changing…" : "Change password"}
          </button>
        </form>
      </Panel>
    </div>
  );
};
